/**
 * DocumentOriginalPreview
 * =======================
 *
 * Renders an uploaded document (PDF / DOCX / TXT) in its ORIGINAL layout,
 * as an alternative to the paragraph-numbered view.
 *
 * - PDF  → rendered with PDF.js (react-pdf) onto our own canvases, so we
 *          fully control the look: white pages, soft shadow, fit-to-width,
 *          and NO browser PDF chrome (the old <iframe> approach showed the
 *          browser's gray toolbar / dark background, which we can't style).
 *          Pages are lazy-rendered (IntersectionObserver) so an 80-page
 *          report doesn't paint 80 canvases at once.
 * - DOCX → rendered client-side with `docx-preview` (stays in-browser; no
 *          external viewer, which matters for PII-bearing documents).
 * - TXT  → shown as preformatted text.
 *
 * Bytes are fetched through our OWN authed API (`previewUrl` is a
 * same-origin proxy path), so there's no CORS issue and no public link.
 * The backend withholds the proxy URL for PII-redacted items, so this
 * component is never asked to show an un-redacted original.
 */
import { Box, CircularProgress, Typography } from '@mui/material';
import axiosServices from '@utils/axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Point PDF.js at its worker (bundled by Vite).
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const backendUrl = import.meta.env.VITE_BACKEND_URL;
const SUPPORTED = new Set(['pdf', 'docx', 'txt']);

/** A single PDF page that only renders its canvas once scrolled near the
 *  viewport — keeps memory/CPU sane on long documents. */
const LazyPdfPage = ({ pageNumber, width, estimatedHeight }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect(); // render once, then keep it
        }
      },
      { root: null, rootMargin: '600px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        mb: 2,
        minHeight: visible ? undefined : estimatedHeight,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {visible ? (
        <Box sx={{ boxShadow: 3, bgcolor: '#fff' }}>
          <Page
            pageNumber={pageNumber}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Box>
      ) : null}
    </Box>
  );
};

const DocumentOriginalPreview = ({ previewUrl, fileExt, height = 'calc(100vh - 170px)' }) => {
  const ext = (fileExt || '').toLowerCase();
  const docxContainerRef = useRef(null);
  const pdfWrapRef = useRef(null);

  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [fileData, setFileData] = useState(null); // ArrayBuffer (pdf) / blob (docx) / string (txt)
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(800);

  // Fetch the bytes once, through our authed same-origin proxy.
  useEffect(() => {
    let cancelled = false;
    if (!previewUrl || !SUPPORTED.has(ext)) {
      setStatus('error');
      return undefined;
    }
    setStatus('loading');
    setFileData(null);
    setNumPages(0);

    const url = `${backendUrl}${previewUrl}`;
    const responseType = ext === 'txt' ? 'text' : 'arraybuffer';

    axiosServices
      .get(url, { responseType })
      .then(async (res) => {
        if (cancelled) return;
        if (ext === 'txt') {
          setFileData(res.data);
          setStatus('ready');
        } else if (ext === 'pdf') {
          // react-pdf wants a stable object; keep the ArrayBuffer.
          setFileData(res.data);
          setStatus('ready');
        } else if (ext === 'docx') {
          const blob = new Blob([res.data], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          });
          const { renderAsync } = await import('docx-preview');
          if (cancelled || !docxContainerRef.current) return;
          docxContainerRef.current.innerHTML = '';
          await renderAsync(blob, docxContainerRef.current, null, {
            className: 'docx-preview',
            inWrapper: true,
          });
          if (!cancelled) setStatus('ready');
        }
      })
      .catch((err) => {
        console.error('Document preview failed:', err);
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [previewUrl, ext]);

  // Track container width so PDF pages fit nicely (capped for big screens).
  useEffect(() => {
    const el = pdfWrapRef.current;
    if (!el || ext !== 'pdf') return undefined;
    const measure = () => {
      const w = el.clientWidth;
      if (w) setPageWidth(Math.min(Math.max(w - 48, 320), 1000));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ext, status]);

  // react-pdf re-parses if `file` identity changes; memoize so scrolling
  // (state updates) doesn't reload the whole document.
  const pdfFile = useMemo(
    () => (ext === 'pdf' && fileData ? { data: fileData } : null),
    [ext, fileData],
  );

  if (status === 'error') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          We couldn&apos;t show the original layout for this file. Switch to the paragraph view to
          read its content.
        </Typography>
      </Box>
    );
  }

  if (ext === 'pdf') {
    return (
      <Box
        ref={pdfWrapRef}
        sx={{
          height,
          minHeight: 600,
          overflow: 'auto',
          bgcolor: 'grey.100',
          borderRadius: 1,
          py: 2,
        }}
      >
        {status === 'loading' && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={26} />
          </Box>
        )}
        {pdfFile && (
          <Document
            file={pdfFile}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CircularProgress size={26} />
              </Box>
            }
            error={
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Couldn&apos;t render this PDF. Switch to the paragraph view instead.
                </Typography>
              </Box>
            }
          >
            {Array.from({ length: numPages }, (_, i) => (
              <LazyPdfPage
                key={i + 1}
                pageNumber={i + 1}
                width={pageWidth}
                estimatedHeight={pageWidth * 1.414}
              />
            ))}
          </Document>
        )}
      </Box>
    );
  }

  if (ext === 'txt') {
    return (
      <Box
        sx={{ height, minHeight: 600, overflow: 'auto', p: 3, bgcolor: 'grey.50', borderRadius: 1 }}
      >
        {status === 'loading' ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Typography
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'inherit',
              fontSize: 14,
              m: 0,
            }}
          >
            {fileData}
          </Typography>
        )}
      </Box>
    );
  }

  // DOCX
  return (
    <Box
      sx={{
        height,
        minHeight: 600,
        overflow: 'auto',
        bgcolor: 'grey.100',
        p: 3,
        borderRadius: 1,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {status === 'loading' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Rendering document…
          </Typography>
        </Box>
      )}
      <Box ref={docxContainerRef} sx={{ '& .docx-wrapper': { background: 'transparent', p: 0 } }} />
    </Box>
  );
};

export default DocumentOriginalPreview;
